import { ReactElement, ReactNode } from 'react';
import clsx from 'clsx';
import { Box, FormControlLabel, makeStyles, useTheme, useMediaQuery } from '@material-ui/core';
import { Colors, FormLabel, HelpTooltip } from '../helpers';

export interface ListBaseProps {
    label?: ReactNode;
    labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
    horizontal?: boolean;
    options: ListBaseOption[];
    outlined?: boolean;
    tooltip?: string;
}

export interface ListBaseOption {
    label: ReactNode;
    control: ReactElement;
    info?: ReactNode;
}

export interface ListOption<T> extends Omit<ListBaseOption, 'control'> {
    disabled?: boolean;
    value: T;
}

export interface ListProps<T> extends Omit<ListBaseProps, 'options'> {
    options: ListOption<T>[];
}

export default (props: ListBaseProps) => {
    const { options, outlined, horizontal, labelPlacement, label, tooltip } = props;
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles({ isMobile: isMobileView });
    return <>
        <FormLabel label={label} tooltip={tooltip} />
        {options.map((option, i) =>
            <FormControlLabel
                key={i}
                classes={{ root: horizontal ? clsx(classes.horizontal, outlined ? clsx(classes.root, classes.outlined) : '') : clsx(classes.root, outlined ? classes.outlined : ''), label: classes.label }}
                control={option.control}
                labelPlacement={labelPlacement || 'start'}
                label={<Box display="flex" alignItems="center">
                    {typeof option.label === 'string' ? <>{option.label} <HelpTooltip title={option.info} /></> : option.label}
                </Box>}
            />
        )}
    </>
}

const useStyles = makeStyles({
    root: {
        padding: '3px 0 3px 0',
        margin: 0,
        display: 'flex'
    },
    horizontal: (props: { isMobile: boolean }) => ({
        flexGrow: 1,
        margin: !props.isMobile ? '0 60px 0 0' : '0 10px 0 0',
        display: 'inline-flex',
        '&:last-child': {
            margin: 0
        }
    }),
    outlined: {
        border: `1px solid ${Colors.Grey}`,
        borderRadius: '0.75rem',
        backgroundColor: Colors.White,
        marginBottom: 10
    },
    label: {
        flexGrow: 1,
        color: Colors.Black,
        paddingRight: 9
    }
}, { name: 'ListBase' });
